/**
 * Monthly Uploads — the supplier side of the upload cycle. The customer has
 * no ERP feed, so each month the supplier sends a Supplier Performance file
 * and a Quality file; this page shows what is due, what is in, and takes the
 * files.
 *
 * The page leads with what is owed: a status banner, the templates each file
 * must follow, then the months still waiting on one. Everything on record —
 * sent and outstanding — sits below in a searchable, filterable table.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, RefreshCw, CalendarCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMyObligations } from './my-uploads/useMyObligations';
import { summariseObligations } from './my-uploads/uploadSummary';
import ObligationPeriodCard from './my-uploads/ObligationPeriodCard';
import UploadHistoryTable from './my-uploads/UploadHistoryTable';
import UploadSummaryBanner from './my-uploads/UploadSummaryBanner';
import TemplatesCard from './my-uploads/TemplatesCard';
import UploadErrorDialog from './my-uploads/UploadErrorDialog';
import { useUploadTemplates } from './my-uploads/useUploadTemplates';

function PeriodSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="h-5 w-32 animate-pulse rounded bg-muted" />
        <div className="h-20 w-full animate-pulse rounded bg-muted" />
        <div className="h-20 w-full animate-pulse rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

/** Small heading above a group of period cards. */
function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function MyUploads() {
  const { user } = useAuth();
  const obligations = useMyObligations();
  const supplierName = user?.companyName;
  const customer = obligations.customerName ?? 'your customer';
  const templates = useUploadTemplates();
  const summary = useMemo(() => summariseObligations(obligations.periods), [obligations.periods]);
  // Only the month currently owed is on screen by default. Older gaps are real
  // but they are not what a supplier came to do today, and three open cards
  // pushed the record below the fold.
  const [showOlderOpen, setShowOlderOpen] = useState(false);
  const openPeriods = showOlderOpen ? summary.openPeriods : summary.openPeriods.slice(0, 1);
  const olderOpenCount = summary.openPeriods.length - 1;

  return (
    <div className="space-y-6">
      <UploadErrorDialog
        failure={obligations.failure}
        onClose={obligations.dismissFailure}
        onDownloadTemplate={(f) => templates.download(f.datasetType)}
      />
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Monthly Uploads{supplierName ? ` — ${supplierName}` : ''}
        </h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          Each month {customer} needs two files from you: a{' '}
          <span className="font-medium text-foreground">Supplier Performance</span> file (deliveries against
          orders) and a <span className="font-medium text-foreground">Quality</span> file (NCRs raised). Drop
          each file on its row — the row is marked uploaded as soon as the file is accepted.
        </p>
      </div>

      {obligations.error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-sm font-medium">Could not load your uploads</p>
            <p className="max-w-md text-sm text-muted-foreground">{obligations.error}</p>
            <Button variant="outline" size="sm" onClick={obligations.retry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : obligations.loading && obligations.periods.length === 0 ? (
        // First paint only. Every upload refetches, and swapping the page for
        // skeletons then unmounts the history table — sending the supplier back
        // to All, page 1, search cleared, mid-correction.
        <div className="space-y-4">
          <PeriodSkeleton />
          <PeriodSkeleton />
        </div>
      ) : obligations.periods.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <CalendarCheck className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">Nothing due yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Your monthly upload periods appear here once {customer} has set them up for your company.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <UploadSummaryBanner summary={summary} />

          <TemplatesCard templates={templates} />

          {summary.openPeriods.length > 0 && (
            <section className="space-y-3">
              <SectionHeading title="Needs a file from you" />
              {openPeriods.map((period) => (
                <ObligationPeriodCard
                  key={period.key}
                  period={period}
                  uploadingId={obligations.uploadingId}
                  onUpload={(row, file) => void obligations.upload(row, file)}
                  onDownloadTemplate={(row) => templates.download(row.dataset_type)}
                />
              ))}
              {olderOpenCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOlderOpen((open) => !open)}
                  data-testid="toggle-older-open"
                >
                  {showOlderOpen
                    ? 'Show only the current month'
                    : `Show ${olderOpenCount} older outstanding month${olderOpenCount === 1 ? '' : 's'}`}
                </Button>
              )}
            </section>
          )}

          <UploadHistoryTable
            periods={obligations.periods}
            uploadingId={obligations.uploadingId}
            onUpload={(row, file) => void obligations.upload(row, file)}
            supplierName={supplierName}
          />
        </div>
      )}
    </div>
  );
}
